import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { api, getSessionCookieToken } from '../api/client'
import { IntegrationLogo } from './IntegrationLogo'

type BitbucketStatus = {
  connected: boolean
  workspace: string | null
  error_code: string | null
}

export function BitbucketAppCard({ onStatusChange }: { onStatusChange?: () => void }) {
  const { getToken } = useAuth()
  const [status, setStatus] = useState<BitbucketStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  const searchParams = new URLSearchParams(window.location.search)
  const setupAction = searchParams.get('setup_action')
  const errorCode = searchParams.get('error_code')

  const authToken = useCallback(async () => {
    const token = getSessionCookieToken() ?? (await getToken())
    if (!token) throw new Error('Not authenticated')
    return token
  }, [getToken])

  const fetchStatus = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await authToken()
      const s = await api.getBitbucketStatus(token)
      setStatus(s)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [authToken])

  useEffect(() => {
    void fetchStatus()
    if (setupAction || errorCode) setExpanded(true)
  }, [fetchStatus, setupAction, errorCode])

  async function handleConnect() {
    setConnecting(true)
    setError(null)
    try {
      const token = await authToken()
      const redirectUri = window.location.origin + window.location.pathname
      const { connect_url } = await api.getBitbucketConnectUrl(token, redirectUri)
      window.location.href = connect_url
    } catch (err) {
      setError((err as Error).message)
      setConnecting(false)
    }
  }

  async function handleDisconnect() {
    if (!window.confirm('Disconnect Bitbucket? This removes all linked OAuth credentials.')) return
    setDisconnecting(true)
    setError(null)
    try {
      const token = await authToken()
      await api.disconnectBitbucket(token)
      await fetchStatus()
      onStatusChange?.()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setDisconnecting(false)
    }
  }

  const isConnected = status?.connected === true

  return (
    <div className={`cred-card ${expanded ? 'cred-card--open' : ''}`} style={{ marginBottom: '8px' }}>
      <button
        type="button"
        className="cred-card-header"
        onClick={() => setExpanded((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
      >
        <span className="cred-card-icon" style={{ display: 'flex', alignItems: 'center' }}>
          <IntegrationLogo integrationId="bitbucket" size={22} />
        </span>
        <span className="cred-card-title" style={{ fontWeight: 600 }}>Bitbucket</span>

        <span
          className="conn-type-badge"
          style={{
            background: 'rgba(93, 184, 114, 0.08)',
            color: 'var(--success)',
            border: '1px solid rgba(93, 184, 114, 0.2)',
          }}
        >
          READ-ONLY (Repos)
        </span>

        <span className="conn-type-badge conn-type-oauth">OAuth</span>

        {loading ? (
          <span className="health-badge health-missing">Checking…</span>
        ) : isConnected ? (
          <span className="health-badge health-ok">
            Connected{status.workspace ? ` · ${status.workspace}` : ''}
          </span>
        ) : (
          <span className="health-badge health-missing">Not connected</span>
        )}

        <span className="cred-card-chevron">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="cred-card-body">
          {setupAction === 'connected' && !errorCode && (
            <p className="verify-result verify-result--ok" style={{ margin: '8px 0 0' }}>
              Bitbucket connected successfully.
            </p>
          )}
          {errorCode === 'oauth_denied' && (
            <p className="verify-result verify-result--error" style={{ margin: '8px 0 0' }}>
              OAuth authorization was denied. Please try again and approve the requested permissions.
            </p>
          )}

          {error && (
            <p className="cred-error" style={{ marginTop: '8px' }}>{error}</p>
          )}

          <div style={{ marginTop: '16px' }}>
            {isConnected ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text)' }}>
                  Bitbucket is connected
                  {status.workspace ? ` to workspace <strong>${status.workspace}</strong>` : ''}.
                  SelfHeal can read repositories and commits to aid investigations.
                </p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="verify-btn"
                    onClick={() => void handleDisconnect()}
                    disabled={disconnecting}
                    style={{ color: 'var(--error)', borderColor: 'var(--error)' }}
                  >
                    {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text)', lineHeight: '1.5' }}>
                  Connect Bitbucket via OAuth to let SelfHeal read your repositories and commits
                  during incident investigations.
                </p>
                <div>
                  <button
                    type="button"
                    className="cred-save-btn"
                    onClick={() => void handleConnect()}
                    disabled={connecting}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M0 1.8C0 .81.81 0 1.8 0h12.4C15.19 0 16 .81 16 1.8v12.4c0 .99-.81 1.8-1.8 1.8H1.8A1.8 1.8 0 0 1 0 14.2V1.8zm8 10.7 5.2-9H9.1L7.3 7.4 5.7 3.5H2.8L8 12.5z" />
                    </svg>
                    Connect Bitbucket
                  </button>
                </div>
              </div>
            )}
          </div>

          <p style={{ marginTop: '16px', fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.4' }}>
            OAuth tokens are stored securely in AWS Secrets Manager per workspace. SelfHeal only reads repository data.
          </p>
        </div>
      )}
    </div>
  )
}
